import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import PropertyAutocomplete from "@/components/PropertyAutocomplete";
import { GitCompareArrows, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";

const SCENARIO_META = {
  sell:              { label: "Sell",                    tone: "border-slate-300" },
  conventional:      { label: "Conventional Rental",     tone: "border-slate-300" },
  hud_vash:          { label: "HUD-VASH",                tone: "border-[#C25E00]" },
  recovery:          { label: "Recovery Housing",        tone: "border-emerald-400" },
  transitional:      { label: "Transitional Housing",    tone: "border-amber-400" },
  master_lease:      { label: "Master Lease to Operator",tone: "border-slate-500" },
  company_operated:  { label: "Company-Operated",        tone: "border-rose-400" },
};
const ALL_SCENARIOS = Object.keys(SCENARIO_META);
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}`;
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const complexityLabel = { none:"None", low:"Low", medium:"Medium", high:"High", very_high:"Very High" };
const complexityTone = { none:"bg-slate-100 text-slate-600", low:"bg-emerald-100 text-emerald-800", medium:"bg-amber-100 text-amber-800", high:"bg-rose-100 text-rose-800", very_high:"bg-rose-200 text-rose-900" };

export default function Scenarios() {
  const [sp] = useSearchParams();
  const [prop, setProp] = useState(null);
  const [selected, setSelected] = useState(["sell","conventional","hud_vash","recovery","transitional","master_lease","company_operated"]);
  const [debt, setDebt] = useState({ down_payment_pct: 25, interest_rate: 7.5, term_years: 30 });
  const [overrides, setOverrides] = useState({}); // {scenarioKey: {opex_line_items: {}, vacancy_pct, ...}}
  const [expanded, setExpanded] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    const pid = sp.get("property");
    if (pid) api.get(`/properties/${pid}`).then(r => setProp(r.data));
    else api.get("/properties").then(r => { if (r.data[0]) setProp(r.data[0]); });
  }, [sp]);

  const run = async () => {
    if (!prop) return;
    const { data } = await api.post("/scenarios/compare", {
      property_id: prop.id, scenarios: selected, overrides, debt,
    });
    setResult(data);
  };
  useEffect(() => { if (prop) { const t = setTimeout(run, 300); return () => clearTimeout(t); } /* eslint-disable-next-line */ }, [prop?.id, selected, overrides, debt]);

  const setOpexLine = (scenarioKey, lineKey, val) => {
    setOverrides({
      ...overrides,
      [scenarioKey]: {
        ...(overrides[scenarioKey] || {}),
        opex_line_items: { ...((overrides[scenarioKey] || {}).opex_line_items || {}), [lineKey]: Number(val) || 0 },
      },
    });
  };

  return (
    <div className="space-y-6" data-testid="scenarios-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2"><GitCompareArrows className="w-3.5 h-3.5"/>Multi-Scenario Comparison</div>
        <h1 className="text-3xl font-bold">Scenarios</h1>
        <p className="text-xs text-slate-500 mt-1">Compare an arbitrary set of use scenarios on a single property. Specialized OpEx uses line items, not a flat %. Scenarios lacking inputs show "Not Yet Modeled".</p>
      </div>

      <Card className="p-5 border-slate-200">
        <Label>Property</Label>
        <PropertyAutocomplete testid="scenarios-property-search" placeholder="Search a property…" onSelect={(p) => setProp(p)} />
        {prop && <div className="text-xs text-slate-600 mt-2">Loaded: <span className="font-semibold">{prop.address}</span> · {prop.city}, TX · {prop.bedrooms}BR/{prop.bathrooms}BA · Acq ${Number(prop.acquisition_cost||0).toLocaleString()}</div>}
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Scenarios to compare</div>
            <div className="space-y-1.5">
              {ALL_SCENARIOS.map(k => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`scenario-select-${k}`}>
                  <input type="checkbox" checked={selected.includes(k)} onChange={e => setSelected(e.target.checked ? [...selected, k] : selected.filter(x => x !== k))} />
                  <span>{SCENARIO_META[k].label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 space-y-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Debt assumptions</div>
            <div>
              <div className="flex justify-between text-xs mb-1"><Label>Down payment</Label><span className="font-mono font-semibold">{debt.down_payment_pct}%</span></div>
              <Slider min={0} max={100} step={5} value={[debt.down_payment_pct]} onValueChange={([v])=>setDebt({...debt, down_payment_pct:v})} data-testid="scenarios-down-slider" />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1"><Label>Interest rate</Label><span className="font-mono font-semibold">{debt.interest_rate}%</span></div>
              <Slider min={3} max={12} step={0.25} value={[debt.interest_rate]} onValueChange={([v])=>setDebt({...debt, interest_rate:v})} data-testid="scenarios-rate-slider" />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1"><Label>Term (years)</Label><span className="font-mono font-semibold">{debt.term_years}</span></div>
              <Slider min={5} max={30} step={5} value={[debt.term_years]} onValueChange={([v])=>setDebt({...debt, term_years:v})} data-testid="scenarios-term-slider" />
            </div>
          </div>
        </div>
      </Card>

      {result && (
        <Card className="p-0 border-slate-200 overflow-hidden" data-testid="scenarios-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-900 text-white text-xs uppercase tracking-wider">
                  <th className="text-left px-3 py-2.5">Scenario</th>
                  <th className="text-right px-3 py-2.5">Gross Revenue</th>
                  <th className="text-right px-3 py-2.5">OpEx</th>
                  <th className="text-right px-3 py-2.5">NOI</th>
                  <th className="text-right px-3 py-2.5">Debt Svc</th>
                  <th className="text-right px-3 py-2.5">Cash Flow</th>
                  <th className="text-right px-3 py-2.5">Cap</th>
                  <th className="text-right px-3 py-2.5">CoC</th>
                  <th className="text-right px-3 py-2.5">DSCR</th>
                  <th className="text-center px-3 py-2.5">Complexity</th>
                  <th className="text-center px-3 py-2.5">Risk</th>
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {result.scenarios.map((s) => {
                  const notModeled = s.readiness === "not_yet_modeled";
                  const isOpen = expanded[s.key];
                  return (
                    <Fragment key={s.key}>
                      <tr className={`border-b border-slate-100 ${notModeled ? "bg-slate-50" : ""}`} data-testid={`scenario-row-${s.key}`}>
                        <td className={`px-3 py-2.5 font-semibold border-l-4 ${SCENARIO_META[s.key]?.tone || ""}`}>{s.label}</td>
                        {notModeled ? (
                          <td colSpan={8} className="px-3 py-2.5 text-xs text-amber-800">
                            <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Not Yet Modeled — missing: {(s.missing_inputs || []).join(", ") || "inputs"}</span>
                          </td>
                        ) : (
                          <>
                            <td className="px-3 py-2.5 text-right font-mono">{money(s.gross_revenue)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-slate-600">{money(s.opex_annual)}</td>
                            <td className="px-3 py-2.5 text-right font-mono font-bold">{money(s.noi)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-slate-600">{money(s.annual_debt_service)}</td>
                            <td className={`px-3 py-2.5 text-right font-mono font-bold ${s.annual_cash_flow >= 0 ? "text-[#C25E00]" : "text-rose-600"}`}>{money(s.annual_cash_flow)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{pct(s.cap_rate_pct)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{pct(s.cash_on_cash_pct)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{s.dscr}</td>
                          </>
                        )}
                        <td className="px-3 py-2.5 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold ${complexityTone[s.operational_complexity]}`}>{complexityLabel[s.operational_complexity] || "—"}</span></td>
                        <td className="px-3 py-2.5 text-center"><span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold ${complexityTone[s.risk_rating]}`}>{complexityLabel[s.risk_rating] || "—"}</span></td>
                        <td className="px-3 py-2.5 text-right">
                          <button onClick={() => setExpanded({ ...expanded, [s.key]: !isOpen })} className="text-slate-500 hover:text-slate-900" data-testid={`scenario-expand-${s.key}`}>
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <td colSpan={12} className="px-6 py-4" data-testid={`scenario-detail-${s.key}`}>
                            {notModeled ? (
                              <div className="text-sm text-amber-800">To model this scenario, capture: {(s.missing_inputs || []).join(", ")}</div>
                            ) : (
                              <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Assumptions</div>
                                  <div className="text-xs text-slate-700 mb-3">{s.assumptions_note}</div>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <Kv k="Capital required" v={money(s.capital_required)} />
                                    <Kv k="Vacancy" v={pct(s.vacancy_pct)} />
                                    <Kv k="Cap rate" v={pct(s.cap_rate_pct)} />
                                    <Kv k="DSCR" v={s.dscr} />
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Line-item OpEx (specialized)</div>
                                  <div className="space-y-1">
                                    {(s.line_items || []).map(li => (
                                      <div key={li.key} className="grid grid-cols-6 gap-2 items-center">
                                        <div className="col-span-3 text-xs">{li.label}<div className="text-[10px] text-slate-500">{li.note}</div></div>
                                        <div className="col-span-2">
                                          <Input type="number" value={((overrides[s.key]||{}).opex_line_items||{})[li.key] ?? li.amount_monthly} onChange={e=>setOpexLine(s.key, li.key, e.target.value)} className="font-mono text-xs h-7" data-testid={`opex-${s.key}-${li.key}`} />
                                        </div>
                                        <div className="col-span-1 text-[10px] text-slate-500 text-right">$/mo</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="text-[11px] text-slate-500 italic">
        Every specialized scenario uses line-item OpEx (utilities, staffing, furnishings, turnover, maintenance, transportation, insurance delta, licensing, compliance, management, resident services, reserves) rather than a percentage of EGI. Adjust any line to reflect your model. Scenarios without required inputs (no rent, no acquisition) are not fabricated.
      </div>
    </div>
  );
}

const Kv = ({ k, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{k}</div>
    <div className="text-sm text-slate-800 font-mono">{v ?? "—"}</div>
  </div>
);
